import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { concatByteArray } from 'src/app/shared/crypto/utility';
import { NanoFeature, isNanoFeatureSupported } from 'src/app/shared/drive-version';
import { FrameSock, onFrameMessage, terminateFrameSock } from 'src/app/shared/nano/frame-sock';
import { AIBoxMessagesType } from 'src/app/shared/nano/nano-requests';
import { NanoService, RequestSession } from 'src/app/shared/nano/nano.service';
import { RoomService } from 'src/app/shared/server-services/room.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { FileDownloaderService } from 'src/app/shared/services/file-downloader.service';
import { PageInfo } from 'src/app/shared/services/page-tab.service';
import { EnvironmentService } from 'src/environments/environment.service';

@Component({
  selector: 'app-airoom',
  templateUrl: './airoom.component.html',
  styleUrl: './airoom.component.scss'
})
export class AIRoomComponent implements AfterViewInit, OnDestroy {
  @Input() data: PageInfo;
  @Input() active: boolean;
  @Output() pageInfoChange: EventEmitter<PageInfo> = new EventEmitter<PageInfo>();
  @ViewChild('iframe') iframe: ElementRef;

  public error: boolean = false;
  public safeIframeSrcUrl?: SafeUrl;
  public frameOrigin: string;
  public nanoVersion?: NanoFeature;
  public wopiSrcUrl?: string;

  public sessionToken?: string;

  public frameSock: FrameSock | undefined | null;
  public readSubscription: RequestSession;

  constructor(
    private nanoService: NanoService,
    private roomService: RoomService,
    private environmentService: EnvironmentService,
    private sanitizer: DomSanitizer,
    private dialogServcie: DialogService,
    public fileDownloaderService: FileDownloaderService
  ) {
    this.frameOrigin = this.environmentService.url_ai_box;
  }

  ngAfterViewInit(): void {
    console.log('data', this.data);

    this.roomService.getNanoSession(this.data.id).then((session) => {
      if (!session) {
        this.error = true;
        return;
      }

      if (!isNanoFeatureSupported(session.version, NanoFeature.AIBOX)) {
        this.error = true;
        console.warn('ai box is not supported by this nano version');
        this.dialogServcie.openAlertDialog(
          marker('Error'),
          marker('AI Box is not supported by this nano version')
        );
        return;
      }

      this.nanoVersion = session.version;

      //let path = this.data.fragment['file'];

      //if (this.data.fragment['path']) path = this.data.fragment['path'] + '/' + path;

      this.nanoService.prepareAIBox(
        this.data.id,
        //path,
        session.version,
        (prepareInfo) => {
          let docOrigin = this.environmentService.url_ai_box;
          // TODO: fix url after version support is present
          let docBaseUrl = docOrigin + '/' + prepareInfo.avt + '/';
          /*this.wopiSrcUrl = 'https://wopi.clarabot.com/wopi/files/' + prepareInfo.wfi; // the wopi subdomain does not exist and this is alwas .com even in dev, this is intentional
          let iframeSrcUrl =
            docBaseUrl +
            '?WOPISrc=' +
            encodeURIComponent(this.wopiSrcUrl) +
            '#at!' +
            prepareInfo.wat;*/
          let iframeSrcUrl = docBaseUrl;
          this.safeIframeSrcUrl = this.sanitizer.bypassSecurityTrustResourceUrl(iframeSrcUrl);
        },
        (e) => {
          this.error = true;
          console.warn('prepare error', e);
          this.dialogServcie.openAlertDialog(
            marker('Error'),
            marker('Error during document prepare. Maybe not supported file type.')
          );
        }
      );
    });
  }

  ngOnDestroy(): void {
    terminateFrameSock(this, 'Destroy')
  }

  @HostListener('window:message', ['$event'])
  onFrameMessage(e: MessageEvent): void {
    /*if (
      e.data?.source != 'angular-devtools-backend' &&
      e.data?.source != 'angular-devtools-content-script'
    ) {
      console.log('msg from iframe', e);
    }*/

    if (!(e && e.data && e.origin === this.frameOrigin)) return;
    if (e.source != this.iframe.nativeElement.contentWindow) return;

    onFrameMessage(e, this);
  }



  public postMessageToFrame(msg: Object) {
    //console.log('to-frame', msg);

    if (!this.iframe) {
      console.warn('there is no iframe to communicate', this.iframe);
      return;
    }

    (<HTMLIFrameElement>this.iframe.nativeElement).contentWindow.postMessage(msg, this.frameOrigin);
  }

  public openDocumentEditor(uri, num) {
    this.nanoService.openAIBox(
      this.data.id,
      //uri,
      this.nanoVersion,
      (token) => {
        this.sessionToken = token;

        this.postMessageToFrame({ ws: 'open', num });

        this.streamMessageFromNanoToFrame();
      },
      (e) => {
        this.error = true;
        console.warn('can not open document editor', e);
      }
    );
  }

  /**
   * DOC-RX
   */
  public streamMessageFromNanoToFrame() {
    let currNum = this.frameSock.num;
    let mySubscription = this.nanoService.readAIBoxMessage(
      this.data.id,
      this.sessionToken,
      this.nanoVersion,
      () => {
        console.log('message stream read done');
        terminateFrameSock(this, 'stream limit');
      },
      (e) => {
        this.error = true;
        console.warn('could not stream the message nano->frame', e);
        terminateFrameSock(this, 'stream error'); // TODO: some errors should be close here, but they can't be discerned from error codes right now
      },
      (curr, max, session, msg) => {
        let incMessage = msg?.response?.messages;
        if (incMessage) {
          /**
           * Maybe (but nut sure) there is a chance, this callback can be called after subscription pause/remove,
           * so overhandle this case
           */
          if (currNum === this.frameSock?.num) {
            this.frameSock.push_to_client(incMessage);
          } else {
            mySubscription.pause(); // use local reference to sub to ensure we're referring to ourselves
          }
        }
        //console.log('msg from nano', incMessage);
      }
    );
    this.readSubscription = mySubscription;
  }

  /**
   * DOC-TX
   */
  public streamMessageFromFrameToNano(msgs: AIBoxMessagesType) {
    //console.log('msgs from frame', msgs);
    this.nanoService.sendAIBoxMessage(
      this.data.id,
      this.sessionToken,
      msgs,
      this.nanoVersion,
      () => {
        //console.log('message stream write done');
      },
      (e) => {
        // we may ignore temporary transport errors, but not sure if that is beneficial
        terminateFrameSock(this, 'Nano error ' + e?.error);
      }
    );
  }

  public closeOpenEditor() {
    this.nanoService.closeAIBox(
      this.data.id,
      this.sessionToken,
      this.nanoVersion,
      () => {
        console.log('editor closed');
      },
      (e) => {
        console.warn('could not close the editor session', e);
      }
    );
    this.readSubscription.pause();
  }


}
